import React, { useCallback, useEffect, useRef, useState } from 'react'

type MobileWelcomeFeature = {
  key: string
  label: string
  icon: React.ReactNode
}

type UseAutoScrollingTickerOptions = {
  resumeDelayMs?: number
  speedPxPerMs?: number
}

export function SparklesIcon({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.75l1.52 4.73a1.5 1.5 0 00.95.95L19.2 10.95l-4.73 1.52a1.5 1.5 0 00-.95.95L12 18.15l-1.52-4.73a1.5 1.5 0 00-.95-.95L4.8 10.95l4.73-1.52a1.5 1.5 0 00.95-.95L12 3.75z"
        fill="currentColor"
      />
      <path
        d="M18.25 4.5l.72 2.03a.75.75 0 00.45.45l2.03.72-2.03.72a.75.75 0 00-.45.45l-.72 2.03-.72-2.03a.75.75 0 00-.45-.45l-2.03-.72 2.03-.72a.75.75 0 00.45-.45l.72-2.03z"
        fill="currentColor"
        opacity="0.72"
      />
    </svg>
  )
}

export const MOBILE_WELCOME_FEATURES: ReadonlyArray<MobileWelcomeFeature> = [
  {
    key: 'analysis',
    label: 'AI-powered analysis',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'reports',
    label: 'Comprehensive reports',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    key: 'podcasts',
    label: 'Podcasts',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.5a5.5 5.5 0 005.5-5.5V11a5.5 5.5 0 10-11 0v2a5.5 5.5 0 005.5 5.5zm0 0v2.75m-3 0h6" />
      </svg>
    ),
  },
]

export function useAutoScrollingTicker({
  resumeDelayMs = 1400,
  speedPxPerMs = 0.04,
}: UseAutoScrollingTickerOptions = {}) {
  const tickerRef = useRef<HTMLDivElement | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const initializationFrameRef = useRef<number | null>(null)
  const lastFrameTimeRef = useRef<number | null>(null)
  const resumeTickerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const baseTickerOffsetRef = useRef(0)
  const isProgrammaticScrollRef = useRef(false)
  const [isTickerPaused, setIsTickerPaused] = useState(false)

  const clearTickerResumeTimeout = useCallback(() => {
    if (resumeTickerTimeoutRef.current !== null) {
      clearTimeout(resumeTickerTimeoutRef.current)
      resumeTickerTimeoutRef.current = null
    }
  }, [])

  const normalizeTickerPosition = useCallback(() => {
    const ticker = tickerRef.current
    if (!ticker) return

    const loopWidth = ticker.scrollWidth / 2
    if (loopWidth <= 0) return

    const loopStart = baseTickerOffsetRef.current
    const loopEnd = loopStart + loopWidth

    if (ticker.scrollLeft < loopStart) {
      ticker.scrollLeft += loopWidth
      return
    }

    if (ticker.scrollLeft >= loopEnd) {
      ticker.scrollLeft -= loopWidth
    }
  }, [])

  const pauseTicker = useCallback(() => {
    clearTickerResumeTimeout()
    setIsTickerPaused(true)
  }, [clearTickerResumeTimeout])

  const resumeTickerSoon = useCallback(() => {
    clearTickerResumeTimeout()
    resumeTickerTimeoutRef.current = setTimeout(() => {
      normalizeTickerPosition()
      setIsTickerPaused(false)
    }, resumeDelayMs)
  }, [clearTickerResumeTimeout, normalizeTickerPosition, resumeDelayMs])

  useEffect(() => {
    const initializeOffset = () => {
      const ticker = tickerRef.current
      if (!ticker) return

      const loopWidth = ticker.scrollWidth / 2
      if (loopWidth <= 0) return

      const centeredOffset = Math.max(0, (loopWidth - ticker.clientWidth) / 2)
      baseTickerOffsetRef.current = centeredOffset
      ticker.scrollLeft = centeredOffset
    }

    initializationFrameRef.current = window.requestAnimationFrame(initializeOffset)
    window.addEventListener('resize', initializeOffset)

    return () => {
      if (initializationFrameRef.current !== null) {
        window.cancelAnimationFrame(initializationFrameRef.current)
        initializationFrameRef.current = null
      }
      window.removeEventListener('resize', initializeOffset)
    }
  }, [])

  useEffect(() => {
    const ticker = tickerRef.current
    if (!ticker || isTickerPaused) {
      lastFrameTimeRef.current = null
      return
    }

    const step = (timestamp: number) => {
      const nextTicker = tickerRef.current
      if (!nextTicker) return

      const loopWidth = nextTicker.scrollWidth / 2
      if (loopWidth <= 0) {
        animationFrameRef.current = window.requestAnimationFrame(step)
        return
      }

      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = timestamp
      }

      const elapsed = timestamp - lastFrameTimeRef.current
      lastFrameTimeRef.current = timestamp

      const loopResetPoint = baseTickerOffsetRef.current + loopWidth
      const nextScrollLeft = nextTicker.scrollLeft + elapsed * speedPxPerMs

      isProgrammaticScrollRef.current = true
      nextTicker.scrollLeft =
        nextScrollLeft >= loopResetPoint
          ? nextScrollLeft - loopWidth
          : nextScrollLeft

      animationFrameRef.current = window.requestAnimationFrame(step)
    }

    animationFrameRef.current = window.requestAnimationFrame(step)

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      lastFrameTimeRef.current = null
    }
  }, [isTickerPaused, speedPxPerMs])

  useEffect(() => {
    return () => {
      clearTickerResumeTimeout()
      if (initializationFrameRef.current !== null) {
        window.cancelAnimationFrame(initializationFrameRef.current)
      }
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [clearTickerResumeTimeout])

  const handleTickerScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false
      return
    }

    pauseTicker()
    resumeTickerSoon()
  }, [pauseTicker, resumeTickerSoon])

  return {
    tickerRef,
    handleTickerScroll,
    pauseTicker,
    resumeTickerSoon,
  }
}
